import { Box } from '@mui/material'
import { AccountType, type CaseloadHistoryObject, type CaseloadInfo } from 'core/types'
import { useEffect, useState } from 'react'
import { CaseloadHistory } from 'Components/CareManagement/CaseloadHistory'
import { RemoveFromCaseload } from 'Pages/CareManagement/RemoveFromCaseload'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { CaseloadAssignmentActions } from 'Components/CareManagement/CaseloadAssignmentActions'
import { AcceptDenyModal } from 'Components/Modal/AcceptDenyModal'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { useRefreshDispatch } from 'ContextProviders/RefreshProvider'

export interface ModalAction {
  visible: boolean
  text: string
  title: string
  actionType: string
  orgId: number | null
  userId: number | null
}
interface AssignmentObject {
  actionType: string
  childID: string | null
  assignedUserID: number | null
  assignedOrgID: number | null
  reasonID: number | null
  reason: string | null
  summary: string | null
  otherApprovalReason: string | null
  providedEducations: string[]
  needsMet: boolean | null
  addressedMostImportant: boolean | null
  connectedToReferral: boolean
  reasonNotMet: string

}

export function CareManagementDisplay (props: { childId: number, childName: string, caseloadInfo: CaseloadInfo | null }): JSX.Element {
  const account = useAccountId()
  const accountid = account?.id ?? 0
  const disp = useRefreshDispatch()
  const refresh = (): void => { disp({ type: 'refresh' }) }

  const [showMenu, setShowMenu] = useState(false)
  const HandleRemoveOrg = async (action: CaseloadHistoryObject): Promise<void> => {
    const assignmentObject: AssignmentObject = {
      actionType: 'RemoveFromOrganizationCaseload',
      assignedOrgID: props.caseloadInfo?.orgId ?? null,
      assignedUserID: null,
      childID: props.childId.toString(),
      otherApprovalReason: action.otherReason,
      reasonID: action.reasonId,
      reason: '',
      summary: action.careSummary,
      providedEducations: action.providedEducations,
      needsMet: action.needsMet,
      addressedMostImportant: action.addressedMostImportant,
      connectedToReferral: action.connectedToReferral,
      reasonNotMet: action.reasonNotMet

    }
    await HandleAssign(assignmentObject)
  }
  const handleUpdate = async (): Promise<void> => {
    const assignmentObject: AssignmentObject = {
      actionType: dialogContent.actionType,
      childID: props.childId.toString(),
      assignedUserID: dialogContent.userId,
      assignedOrgID: dialogContent.orgId,
      reasonID: null,
      summary: null,
      reason: '',
      otherApprovalReason: null,
      providedEducations: [],
      addressedMostImportant: null,
      connectedToReferral: false,
      needsMet: null,
      reasonNotMet: ''

    }
    await HandleAssign(assignmentObject)
  }
  const HandleAssign = async (assignmentObject: AssignmentObject): Promise<void> => {
    const { response: result } = await sendPost('/CareManagement/UpdateCaseload', assignmentObject)
    if (result.errors.length === 0) {
      setDialogContent({ ...dialogContent, visible: false })
      refresh()
      setShowRemoveFromOrgDialog(false)
      toast.success(result.message)
    } else {
      let errorString: string = ''
      const errors: Array<{ error: string }> = result.errors
      errors.forEach(e => {
        errorString = errorString + e.error + '\r\n'
      })
      toast.error(errorString)
    }
  }
  const HideRemoveDialog = (): void => {
    setShowRemoveFromOrgDialog(false)
  }

  const onHide = (): void => {
    setShowMenu(false)
  }
  const onShow = (): void => {
    setShowMenu(true)
  }
  const [showRemoveFromOrgDialog, setShowRemoveFromOrgDialog] = useState(false)
  const [dialogContent, setDialogContent] = useState < ModalAction >({ visible: false, actionType: '', text: '', title: '', orgId: null, userId: null })

  const [showHistory, setShowHistory] = useState(false)
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: number, name: string }> | null>(null)

  useEffect(() => {
    const getData = async (): Promise<void> => {
      if (dialogContent != null && dialogContent.actionType === 'AssignToUserCaseload') {
        let endpoint = `/CareManagement/GetUsers?spId=${accountid}`
        if (account?.type === AccountType.ISD) {
          endpoint = `/CareManagement/GetIsdIntakeUsers?isdid=${accountid}`
        }
        const result = await sendGet(endpoint, {})
        if (result.success) {
          setAvailableUsers(result.response)
        }
      } else {
        setAvailableUsers(null)
      }
    }
    void getData()
  }, [accountid, dialogContent.actionType])

  const DialogContent = <Box>
    {dialogContent.text}
    {availableUsers != null && (
      <SelectWithLabel
        width='100%'
        options={availableUsers}
        value={dialogContent.userId ?? ''}
        onChange={(e: any) => {
          setDialogContent({ ...dialogContent, userId: e.target.value })
        }} name={'userSelect'}
      />
    )}
  </Box>

  return <>
    <CaseloadHistory
      key={props.childId}
      childId={props.childId.toString()}
      visible={showHistory}
      onHide={() => { setShowHistory(false) }}
    />
    {<RemoveFromCaseload
      childID={props.childId}
      caseloadInfo={props.caseloadInfo}
      onClose={HideRemoveDialog}
      onSubmit={HandleRemoveOrg}
      visible={showRemoveFromOrgDialog}
      childName={props.childName}
    />}

    <CaseloadAssignmentActions
      isShown ={showMenu}
      onShow={onShow}
      onHide={onHide}
      caseloadInfo={props.caseloadInfo}
      onActionTaken={(data: ModalAction) => {
        setDialogContent(data)
        onHide()
      }}
      onRemoveOrg={() => {
        setShowRemoveFromOrgDialog(true)
        onHide()
      }}
      viewHistory={() => {
        setShowHistory(true)
        onHide()
      }}
    />
    <AcceptDenyModal
      dialogContent={DialogContent}
      onClose={() => {
        setDialogContent({ ...dialogContent, visible: false })
        onHide()
      }}
      onAccept={handleUpdate}
      open={dialogContent.visible}
      hideCancelButton={false}
      title={dialogContent.title}
      maxWidth={'sm'}
    />
  </>
}
